import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Header from './components/Header';
import HuddyFeedback from './components/HuddyFeedback';
import Dashboard from './pages/Dashboard';
import Wallet from './pages/Wallet';
import DEX from './pages/DEX';
import Bridge from './pages/Bridge';
import Faucet from './pages/Faucet';
import Explorer from './pages/Explorer';
import Staking from './pages/Staking';
import Settings from './pages/Settings';
import Compound from './pages/Compound';
import LocalSSO from './pages/LocalSSO';
import useMetaMask from './hooks/useMetaMask';

const SSO_SESSION_KEY = 'metani_sso_session';

const hasSsoSession = () => {
  try {
    const rawSession = window.sessionStorage.getItem(SSO_SESSION_KEY);
    if (!rawSession) return false;

    const session = JSON.parse(rawSession);
    return Boolean(session?.ssoToken || session?.authenticatedAt);
  } catch {
    return false;
  }
};

const buildRedirectTarget = (pathname, search, hash) =>
  encodeURIComponent(`${pathname || '/dashboard'}${search || ''}${hash || ''}`);

function RequireSsoSession({ children }) {
  const location = useLocation();

  if (!hasSsoSession()) {
    const redirect = buildRedirectTarget(location.pathname, location.search, location.hash);
    return <Navigate to={`/sso?redirect=${redirect}`} replace />;
  }

  return children;
}

function HomeRedirect() {
  const location = useLocation();

  if (hasSsoSession()) {
    return <Navigate to="/dashboard" replace />;
  }

  const redirect = buildRedirectTarget('/dashboard', location.search, location.hash);
  return <Navigate to={`/sso?redirect=${redirect}`} replace />;
}

export default function App() {
  const wallet = useMetaMask();

  return (
    <BrowserRouter>
      <div className="min-h-screen bg-slate-950 text-white">
        <Header wallet={wallet} />
        <main className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <Routes>
            <Route path="/" element={<HomeRedirect />} />
            <Route
              path="/dashboard"
              element={
                <RequireSsoSession>
                  <Dashboard wallet={wallet} {...wallet} />
                </RequireSsoSession>
              }
            />
            <Route
              path="/wallet"
              element={
                <RequireSsoSession>
                  <Wallet wallet={wallet} {...wallet} />
                </RequireSsoSession>
              }
            />
            <Route
              path="/dex"
              element={
                <RequireSsoSession>
                  <DEX wallet={wallet} {...wallet} />
                </RequireSsoSession>
              }
            />
            <Route
              path="/bridge"
              element={
                <RequireSsoSession>
                  <Bridge wallet={wallet} {...wallet} />
                </RequireSsoSession>
              }
            />
            <Route
              path="/faucet"
              element={
                <RequireSsoSession>
                  <Faucet wallet={wallet} {...wallet} />
                </RequireSsoSession>
              }
            />
            <Route
              path="/explorer"
              element={
                <RequireSsoSession>
                  <Explorer wallet={wallet} {...wallet} />
                </RequireSsoSession>
              }
            />
            <Route
              path="/staking"
              element={
                <RequireSsoSession>
                  <Staking wallet={wallet} {...wallet} />
                </RequireSsoSession>
              }
            />
            <Route
              path="/compound"
              element={
                <RequireSsoSession>
                  <Compound wallet={wallet} {...wallet} />
                </RequireSsoSession>
              }
            />
            <Route
              path="/settings"
              element={
                <RequireSsoSession>
                  <Settings wallet={wallet} {...wallet} />
                </RequireSsoSession>
              }
            />
            <Route path="/sso" element={<LocalSSO wallet={wallet} {...wallet} />} />
            <Route path="/login" element={<LocalSSO wallet={wallet} {...wallet} />} />
            <Route path="/register" element={<LocalSSO wallet={wallet} {...wallet} initialMode="register" />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
        <HuddyFeedback wallet={wallet} />
      </div>
    </BrowserRouter>
  );
}
